import type { InferContractRouterOutputs } from '@orpc/contract'
import { useInfiniteQuery } from '@tanstack/react-query'
import { uniqBy } from 'lodash-es'
import type { baseContract } from 'pixai-contract/api/orpc'
import { useMemo } from 'react'
import type { ArtworkPreviewFragment } from 'src/services/apollo/generated/graphql'
import { orpc } from './contract'
import { toArtworkPreviewFragment } from './utils/typeAdapters'

type FollowingNewsArtworksPage = InferContractRouterOutputs<
  typeof baseContract
>['artwork']['getFollowingNewsArtworks']

const getNextPageParam = (lastPage: FollowingNewsArtworksPage) => {
  if (!lastPage.pageInfo.hasNextPage) return undefined
  return lastPage.pageInfo.endCursor
}

interface FollowingNewsArtworksResult {
  artworks: ArtworkPreviewFragment[]
  pageInfo: {
    hasNextPage: boolean
    endCursor?: string
  }
  loading: boolean
  loadingMore: boolean
  fetchMore: () => void
}

export function useOrpcFollowingNewsArtworks(
  args: { first: number } | null,
): FollowingNewsArtworksResult {
  const enabled = args != null
  const first = args?.first

  const result = useInfiniteQuery({
    ...orpc.artwork.getFollowingNewsArtworks.infiniteOptions({
      input: (pageParam: string | undefined) => ({
        first,
        after: pageParam,
      }),
      getNextPageParam,
      initialPageParam: undefined as string | undefined,
    }),
    enabled,
    // The following set + `liked` flags are user-scoped — drop cache across
    // login/logout so a returning user doesn't see the previous account's
    // feed (or stale `liked` flags) until the next refetch.
    meta: { dependsOn: ['login'] },
  })

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    result

  const artworks = useMemo(
    () =>
      uniqBy(
        data?.pages.flatMap(page =>
          page.data.map(item => toArtworkPreviewFragment(item)),
        ) ?? [],
        'id',
      ),
    [data?.pages],
  )
  const lastPage = data?.pages.at(-1)

  return {
    artworks,
    pageInfo: {
      hasNextPage: hasNextPage ?? false,
      endCursor: lastPage?.pageInfo.endCursor,
    },
    loading: isLoading,
    loadingMore: isFetchingNextPage,
    fetchMore: fetchNextPage,
  }
}
